
import React, { useState, useEffect, useCallback } from 'react'
import cx from 'classnames'

import useSWR from 'swr'
import { fetchBanner } from 'api/banner'
import type { AxiosResponseType, CommonError } from 'types/api/responses/common'
import type { BannerAPIResponse, BannerType } from 'types/api/responses/banner'

import markdown from 'logic/markdown'

import './styles.sass'

type BannerPresentType = {
	status: 'bannerPresent',
	banner: BannerType,
}

type BannerState =
	| {
			status: 'noBanner',
	  }
	| BannerPresentType

function isSafeBannerActionUrl(url?: string | null): url is string {
	if (!url) return false

	const trimmedUrl = url.trim()
	if (!trimmedUrl || trimmedUrl.startsWith('//')) return false
	if (trimmedUrl.startsWith('/')) return true

	try {
		const parsedUrl = new URL(trimmedUrl)
		return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
	} catch {
		return false
	}
}

export default function Banner() {
	const { data } = useSWR<AxiosResponseType<BannerAPIResponse>, CommonError>('/api/banner', fetchBanner, {
		shouldRetryOnError: false,
	})
	const [bannerState, setBannerState] = useState<BannerState>({ status: 'noBanner' })
	useEffect(() => {
		if (data?.data?.banners?.length) return setBannerState({ status: 'bannerPresent', banner: data.data.banners[0] })
	}, [data])

	const classNames = useCallback(
		(banner: BannerType) =>
			cx('BannerContainer', {
				'BannerContainer--announcement': banner.banner_type === 'announcement',
				'BannerContainer--issue': banner.banner_type !== 'announcement',
				'BannerContainer--imageOnly': banner.display_mode === 'image_only' && Boolean(banner.image_url),
				'BannerContainer--iconText': banner.display_mode !== 'image_only' || !banner.image_url,
			}),
		[],
	)
	const renderMarkdown = useCallback(
		(content: string) => ({
			__html: markdown.parse(content),
		}),
		[],
	)
	return bannerState.status === 'bannerPresent' ? (
		<div
			data-testid={bannerState.banner.banner_type === 'announcement' ? 'banner-with-announcement' : 'banner-with-issue'}
			className={classNames(bannerState.banner)}>
			{bannerState.banner.display_mode === 'image_only' && bannerState.banner.image_url ? (
				isSafeBannerActionUrl(bannerState.banner.action_url) ? (
					<a
						className='BannerContainer__imageOnlyLink'
						href={bannerState.banner.action_url}
						target='_blank'
						rel='noopener noreferrer'
						aria-label={bannerState.banner.action_label || bannerState.banner.content || 'Banner'}>
						<img className='BannerContainer__imageOnlyImage' src={bannerState.banner.image_url} alt='' loading='lazy' />
					</a>
				) : (
					<div className='BannerContainer__imageOnlyWrapper'>
						<img className='BannerContainer__imageOnlyImage' src={bannerState.banner.image_url} alt='' loading='lazy' />
					</div>
				)
			) : (
				<>
					{bannerState.banner.image_url && (
						<img className='BannerContainer__image' src={bannerState.banner.image_url} alt='' loading='lazy' />
					)}
					<div className='BannerContainer__content'>
						<p dangerouslySetInnerHTML={renderMarkdown(bannerState.banner.content)}></p>
						{isSafeBannerActionUrl(bannerState.banner.action_url) && (
							<a
								className='BannerContainer__action'
								href={bannerState.banner.action_url}
								target='_blank'
								rel='noopener noreferrer'>
								{bannerState.banner.action_label || 'Learn more'}
							</a>
						)}
					</div>
				</>
			)}
		</div>
	) : null
}
